import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileToDataUrl } from "../../lib/admin";

export default function AdminHomepage() {
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState("_all");
  const [form, setForm] = useState({
    hero_title: "",
    hero_subtitle: "",
    hero_image: "",
    featured_product_ids: [],
    youtube_videos: [],
  });
  const [newVid, setNewVid] = useState({ title: "", url: "" });

  useEffect(() => {
    Promise.all([api.get("/brands"), api.get("/products")]).then(([b, p]) => {
      setBrands(b.data);
      setProducts(p.data);
    });
  }, []);

  useEffect(() => {
    api.get(`/homepage/${selected}`).then((r) => {
      const d = r.data || {};
      setForm({
        hero_title: d.hero_title || "",
        hero_subtitle: d.hero_subtitle || "",
        hero_image: d.hero_image || "",
        featured_product_ids: d.featured_product_ids || [],
        youtube_videos: d.youtube_videos || [],
      });
    });
  }, [selected]);

  const save = async () => {
    try {
      await api.put(`/homepage/${selected}`, { ...form, brand_slug: selected });
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onHero = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setForm({ ...form, hero_image: url });
  };

  const toggleProduct = (id) => {
    const arr = form.featured_product_ids;
    setForm({
      ...form,
      featured_product_ids: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id],
    });
  };

  const addVid = () => {
    if (!newVid.url) return;
    setForm({ ...form, youtube_videos: [...form.youtube_videos, newVid] });
    setNewVid({ title: "", url: "" });
  };

  const removeVid = (idx) => {
    setForm({ ...form, youtube_videos: form.youtube_videos.filter((_, i) => i !== idx) });
  };

  const filteredProducts =
    selected === "_all" ? products : products.filter((p) => brands.find((b) => b.slug === selected)?.id === p.brand_id);

  return (
    <div data-testid="admin-homepage">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Content</div>
          <h1 className="font-display text-4xl tracking-tighter">Homepage Content</h1>
        </div>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="adm-input min-w-[200px]" data-testid="homepage-brand-select">
          <option value="_all">Main Site (All)</option>
          {brands.map((b) => <option key={b.id} value={b.slug}>{b.name}</option>)}
        </select>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Hero Section</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Hero Title</label>
            <input value={form.hero_title} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} className="adm-input" data-testid="homepage-hero-title" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Hero Image</label>
            <div className="flex items-center gap-3">
              {form.hero_image && <img src={form.hero_image} className="h-10 w-16 object-cover" alt="" />}
              <input type="file" accept="image/*" onChange={(e) => onHero(e.target.files?.[0])} className="text-xs text-zinc-400" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Hero Subtitle</label>
            <textarea rows={3} value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} className="adm-input" />
          </div>
        </div>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Featured Products</div>
        <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
          {filteredProducts.length === 0 && <div className="text-zinc-500 text-sm">No products available.</div>}
          {filteredProducts.map((p) => (
            <button key={p.id} type="button" onClick={() => toggleProduct(p.id)} className={`px-3 py-1.5 text-xs border ${form.featured_product_ids.includes(p.id) ? "bg-white/10 border-white/40" : "border-white/10 hover:bg-white/5"}`}>
              {p.name}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">YouTube Videos</div>
        <div className="space-y-2 mb-4">
          {form.youtube_videos.map((v, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
              <div className="text-sm">{v.title}</div>
              <div className="text-xs text-zinc-500 truncate">{v.url}</div>
              <button onClick={() => removeVid(idx)} className="text-zinc-400 hover:text-red-400 p-2"><X size={14} /></button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-[1fr_2fr_auto] gap-2">
          <input placeholder="Video title" value={newVid.title} onChange={(e) => setNewVid({ ...newVid, title: e.target.value })} className="adm-input" />
          <input placeholder="YouTube URL" value={newVid.url} onChange={(e) => setNewVid({ ...newVid, url: e.target.value })} className="adm-input" />
          <button type="button" onClick={addVid} className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5"><Plus size={14} /></button>
        </div>
      </div>

      <button onClick={save} className="brand-btn px-6 py-3 text-xs uppercase tracking-[0.2em]" data-testid="homepage-save-btn">Save Changes</button>
      <style>{`.adm-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); padding: 0.625rem 0.875rem; font-size: 0.875rem; color: white; outline: none; } .adm-input:focus { border-color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}
